import {
  Dribbble,
  Facebook,
  Github,
  Globe,
  Instagram,
  Linkedin,
  Mail,
  MessageCircle,
  Music,
  Phone,
  Rss,
  Send,
  Slack,
  Twitch,
  Twitter,
  Youtube,
} from 'lucide-react'

/* =========================================================================
   Social icon resolution
   -------------------------------------------------------------------------
   A super admin types a platform name ("Instagram", "LinkedIn Page") and a
   URL; we work out which icon to show. Matching is by substring so a label
   like "LinkedIn Page" still lands on the LinkedIn mark, and falls back to
   the link's host before finally settling on a generic globe.
   ========================================================================= */

/** Longest tokens first — "linkedin" must win before a shorter token can. */
const TOKENS = [
  ['whatsapp', MessageCircle],
  ['instagram', Instagram],
  ['dribbble', Dribbble],
  ['telegram', Send],
  ['linkedin', Linkedin],
  ['facebook', Facebook],
  ['pinterest', Globe],
  ['youtube', Youtube],
  ['twitter', Twitter],
  ['threads', Instagram],
  ['discord', MessageCircle],
  ['behance', Dribbble],
  ['twitch', Twitch],
  ['github', Github],
  ['tiktok', Music],
  ['slack', Slack],
  ['email', Mail],
  ['phone', Phone],
  ['blog', Rss],
  ['mail', Mail],
  ['rss', Rss],
  ['call', Phone],
  ['fb', Facebook],
  ['yt', Youtube],
]

/** Exact-match only — too short to search for inside other words. */
const EXACT = {
  x: Twitter,
  in: Linkedin,
  ig: Instagram,
}

const HOSTS = [
  ['instagram.com', Instagram],
  ['facebook.com', Facebook],
  ['fb.com', Facebook],
  ['linkedin.com', Linkedin],
  ['youtube.com', Youtube],
  ['youtu.be', Youtube],
  ['twitter.com', Twitter],
  ['x.com', Twitter],
  ['github.com', Github],
  ['dribbble.com', Dribbble],
  ['behance.net', Dribbble],
  ['twitch.tv', Twitch],
  ['slack.com', Slack],
  ['t.me', Send],
  ['telegram.me', Send],
  ['wa.me', MessageCircle],
  ['whatsapp.com', MessageCircle],
  ['discord.gg', MessageCircle],
  ['tiktok.com', Music],
]

const normalise = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '')

/**
 * Pick an icon for a platform name, falling back to the URL's host.
 * Always returns a component, so a link never renders without a mark.
 */
export function resolveSocialIcon(name, url = '') {
  const key = normalise(name)

  if (key && EXACT[key]) return EXACT[key]
  for (const [token, Icon] of TOKENS) {
    if (key.includes(token)) return Icon
  }

  const host = String(url || '')
    .replace(/^[a-z][a-z0-9+.-]*:\/\//i, '')
    .split('/')[0]
    .toLowerCase()
  if (host) {
    for (const [domain, Icon] of HOSTS) {
      if (host === domain || host.endsWith('.' + domain)) return Icon
    }
  }

  return Globe
}

/** Renders the resolved mark for a platform. */
export default function SocialIcon({ name, url, className = 'h-4 w-4' }) {
  const Icon = resolveSocialIcon(name, url)
  return <Icon className={className} aria-hidden="true" />
}
